// ============================================================================
// A drafted board action (item #737; TCSD-0416): what its card says, and the
// exact body the command center's own button sends. Pure, so both are tested.
//
// ⛔ "Do it" sends the SAME write the button makes, through the same bridge, and
// the bridge decides what it writes and refuses what it would refuse. Nothing
// here writes; nothing here decides.
// ============================================================================
import { PUSHBACK_REASONS, PRIORITY_WORDS } from '../../../supabase/functions/build-room-bridge/handler';

export type BoardActionName = 'approve' | 'pushback' | 'park' | 'unpark' | 'priority' | 'pin' | 'unpin';

export interface BoardActionInput {
  action: BoardActionName;
  item: number;
  item_title?: string;
  choice?: 'recommendation' | 'own' | null;
  words?: string | null;
  route?: 'cowork' | 'builder' | null;
  reason?: string | null;
  to?: string | null;
  recommended_option?: string | null;
  build_route?: string | null;
  has_write_up?: boolean;
}

export const BOARD_ACTION_TITLES: Record<BoardActionName, string> = {
  approve: 'Approve',
  pushback: 'Push back',
  park: 'Park',
  unpark: 'Unpark',
  priority: 'Change priority',
  pin: 'Pin to the top',
  unpin: 'Unpin',
};

/** The card's one line: what pressing Do it will do, in plain words. */
export function boardActionLine(i: BoardActionInput): string {
  const t = i.item_title ? `"${i.item_title}"` : 'this item';
  const words = (i.words ?? '').trim();
  switch (i.action) {
    case 'approve': {
      const what =
        i.choice === 'own'
          ? `your option, in your words: ${words}`
          : `the recommendation${i.recommended_option ? ` (${i.recommended_option})` : ''}`;
      return `Approve ${t}: ${what}. ${i.route === 'builder' ? 'The Builder builds it.' : 'Built in Cowork.'}`;
    }
    case 'pushback':
      return `Send ${t} back: ${PUSHBACK_REASONS[String(i.reason)] ?? 'pick a reason'}.${words ? ` Your words: ${words}` : ''}`;
    case 'park':
      return `Park ${t}. It moves to the bottom of Needs you.${words ? ` Your note: ${words}` : ''}`;
    case 'unpark':
      return `Unpark ${t}.${words ? ` Your note: ${words}` : ''}`;
    case 'priority':
      return `Set ${t} to: ${PRIORITY_WORDS[String(i.to)] ?? 'pick a priority'}.`;
    case 'pin':
      return `Pin ${t} to the top of Needs you.`;
    case 'unpin':
      return `Unpin ${t}.`;
  }
}

/** The body the button sends: only the fields its action uses, nothing empty. */
export function boardWriteBody(i: BoardActionInput): { action: BoardActionName; item: number } & Record<string, unknown> {
  const body: { action: BoardActionName; item: number } & Record<string, unknown> = { action: i.action, item: Number(i.item) };
  const words = (i.words ?? '').trim();
  if (words && ['approve', 'pushback', 'park', 'unpark'].includes(i.action)) body.words = words;
  if (i.action === 'approve') {
    body.choice = i.choice ?? 'recommendation';
    body.route = i.route ?? 'cowork';
  }
  if (i.action === 'pushback') body.reason = i.reason;
  if (i.action === 'priority') body.to = i.to;
  return body;
}
